/*
Usage Notes: 

The following web component can be dropped into any standard Shopify theme (i.e. non-headless), using Liquid to pass product data:

  {% assign skioSellingPlanGroups = product.selling_plan_groups | where: 'app_id', 'SKIO' %}
  {% if skioSellingPlanGroups.size > 0 %}
    <skio-plan-picker 
      product='{{ product | json | escape }}'
      selectedVariant='{{ product.selected_or_first_available_variant | json | escape }}'
      formId='{{ product_form_id }}'
      currency='{{ cart.currency.iso_code }}'
      >
    </skio-plan-picker>
    <input type="hidden" aria-hidden="true" name="selling_plan" value="">
    <script src="{{ 'skio-plan-picker-component.js' | asset_url }}" type="module"></script>
  {% endif %}

  Note: formId not required if element is inside of a form already

  Example variantChanged event dispatch:
  document.dispatchEvent( new CustomEvent("variantChanged", { detail: { variantId: variant.id } }) );
*/

import { LitElement, html, css } from 'https://cdn.jsdelivr.net/gh/lit/dist@2/core/lit-core.min.js';

const skioStyles = css`
  .skio-plan-picker {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 0;
    border: 0;
  }
  .skio-onetime-second {
    order: 2;
  }
  
  .skio-group-container {
    display: none;
  }
  .skio-group-container--available {
    display: block;
    position: relative;
    border-radius: 10px;
    border: 2px solid rgb(247, 245, 242);
    transition: border-color 0.2s ease;
    background-color: var(--skio-background-color);
  }
  .skio-group-container--selected {
    background-color: var(--skio-selected-background-color);
    border: 2px solid var(--skio-selected-border-color, var(--skio-selected-background-color)) !important;
  }
  .skio-group-container--selected .skio-additional-item svg path {
    fill: var(--skio-selected-text-color);
  }
  .skio-group-container--selected .skio-radio__container svg circle:not(.skio-radio) {
    stroke: var(--skio-selected-radio-color);
  }
  .skio-group-container--selected .skio-group-title,
  .skio-group-container--selected .skio-additional-item,
  .skio-group-container--selected .skio-additional-item-text,
  .skio-group-container--selected .skio-group-purpose,
  .skio-group-container--selected .skio-learn-more {
    color: var(--skio-selected-text-color);
  }
    
  .skio-group-container--selected .skio-price {
    color: var(--skio-selected-price-color);
  }
  .skio-group-container--selected .skio-price del {
    color: var(--skio-selected-compare-at-price-color);
  }
  .skio-group-input {
    position: absolute;
    width: 0px;
    height: 0px;
    opacity: 0;
  }
  .skio-group-input:focus-visible ~ .skio-group-label {
    outline: 2px #ccc solid;
    outline-offset: 4px;
    border-radius: 5px;
  }
  
  .skio-group-label {
    display: flex;
    flex-direction: column;
    cursor: pointer;
    padding: 10px;
    overflow: hidden;
  }
  
  .skio-group-topline {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    width: 100%;
    font-size: 16px;
    font-family: var(--heading-font-family) !important;
  }
  
  .skio-radio__container {
    display: flex;
    margin-right: 10px;
  }

  .skio-radio__container svg circle.skio-radio {
    fill: var(--skio-radio-border-color);
  }

  .skio-radio__container svg circle:not(.skio-radio) {
    stroke: var(--skio-radio-color);
  }
  
  .skio-radio {
    transition: transform 0.25s cubic-bezier(0.4,0,0.2,1), opacity 0.25s cubic-bezier(0.4,0,0.2,1);
    transform-origin: center;
    transform: scale(0);
    opacity: 0;
  }
  .skio-group-label:hover .skio-radio {
    transform: scale(0.8);
    opacity: 0.75;
  }
  .skio-group-container--selected .skio-group-label .skio-radio {
    transform: scale(0.8);
    opacity: 1;
    fill: var(--skio-selected-radio-border-color);
  }
  
  .skio-price {
    color: var(--skio-price-color);
    margin-left: auto;
    text-transform: none !important;
    font-family: var(--text-font-family) !important;
  }

  .skio-price del {
    color: var(--skio-compare-at-price-color);
    font-size: 14px;
    line-height: 27.73px;
    margin-right: 4px;
  }
  
  .skio-group-content {
    display: none;
    width: auto;
    margin-left: 30px;
    transition: max-height 0.25s cubic-bezier(0.4,0,0.2,1),
                opacity 0.25s cubic-bezier(0.4,0,0.2,1);
    max-height: 38px;
    opacity: 1;
  }
  
  /* Hide frequency if not selected */
  /* .skio-group-container:not(.skio-group-container--selected) .skio-group-content {
    max-height: 0;
    opacity: 0;
    pointer-events: none;
  } */
  
  .skio-group-title {
    font-family: var(--heading-font-family);
    font-weight: var(--heading-font-weight);
    font-style: var(--heading-font-style);
    font-size: 18px;
    line-height: 1.2;
    text-transform: lowercase;
    color: var(--skio-text-color);
    min-width: max-content;
  }
  
  .skio-save {
    color: #0fa573;
    border: 1px #0fa573 solid; 
    padding: 0px 8px;
    border-radius: 20px;
  }
  
  .skio-frequency {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 8px 30px 8px 10px;
    margin-top: 5px;
    border-radius: 5px;
    background-color: rgb(255, 255, 255);
    width: 40%;
    border: 1px solid rgb(247, 245, 242);
    font-size: 14px;
    font-family: inherit;
    white-space: nowrap;
    text-overflow: ellipsis;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7' /%3E%3C/svg%3E");
    background-position: right 10px top 50%;
    background-size: 18px;
    background-repeat: no-repeat;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    min-width: 180px;
  }
  .skio-frequency.skio-frequency--one {
    background-image: none;
    pointer-events: none;
  }
  
  .skio-frequency span {
    text-transform: lowercase;
  }

  button.add-to-cart {
    justify-content: center;
    font-weight: 400;
    text-transform: capitalize;
    letter-spacing: 0;
    font-style: normal;
    font-size: 16px;
    text-decoration: none;
    box-shadow: 0 0 #0000004d inset;
    width: 100%;
    color: #fff;
    background-color: #000;
    border-radius: 5px;
    transition-duration: .3s;
    padding: 10px 32px;
    text-transform: uppercase;
    cursor: pointer;
    font-family: 'FK Grotesk Neue Trial';
    transition: border-color 0.25s ease, background-color 0.25s ease, color 0.25s ease;

    border: solid 2px rgba(0,0,0,0);
  }
  button.add-to-cart:hover {
    background-color: rgba(0,0,0,0);
    color: #000;
    border-color: #000;
  }

  .skio-details {
    --text-color: #333;
    --text-color-secondary: #888; 
    
    user-select: none;
    -webkit-user-select: none;
    margin-bottom: 20px;
    order: 3;
  }

  .skio-details summary::-webkit-details-marker,
  .skio-details summary::marker,
  .skio-details slot {
    color: rgba(0,0,0,0) !important;
  }

  .skio-details summary {
    margin-top: 15px;
  }

  .skio-details summary span {
    font-size: 0.9em;
    display: flex;
    padding: .5em 0;
    cursor: pointer;
    align-items: center;
    gap: 10px;
    text-decoration: underline;

    margin-top: -40px;
    color: #000;
  }

  @keyframes fadeInDown {
    0% {
      opacity: 0;
      transform: translateY(-15px);
    }
    100% {
      opacity: 1;
      transform: translateY(0px);
    }
  }
  .skio-details[open] > .skio-details--content {
    animation-name: fadeInDown;
    animation-duration: 0.3s;
  }

  .skio-details--content {
    position: absolute;
    z-index: 1020;
    padding: 1em;
    width: fit-content;
    border-radius: 5px;
    background: white;
    box-shadow: 0 0 5px rgb(23 24 24 / 5%), 0 1px 2px rgb(0 0 0 / 7%);
  }

  .skio-details ul {
    margin: 0;
    padding: 0;
  }

  .skio-details ul li {
    display: flex;
    align-items: flex-start;
    gap: .75em;

    margin-bottom: 1em;
  }

  .skio-details .skio-content {
    display: flex;
    flex-direction: column;
  }

  .skio-details .skio-content p {
    font-size: 0.9em;

    margin-top: 0;
    margin-bottom: 0;

    letter-spacing: 0;
    line-height: 1.5;

    color: #374437;
  }

  .skio-details ul li small {
    font-size: 0.7em;
    color: var(--text-color-secondary);
  }

  .skio-details .skio-icon {
    display: flex;

    width: 2.25em;
    height: 2.25em;

    color: #374437;
    background: #f8f8f8;
    border-radius: 100%;

    flex-shrink: 0;
    align-items: center;
    justify-content: center;
  }

  .skio-details .skio-icon svg {
    width: 1.25em;
    height: 1.25em;

    color: inherit;
  }

  .skio-details--footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 0.9em;
  }

  .skio-details--footer a {
    color: #374437;
  }

  .skio-manage-link {
    text-decoration: underline;
  }

  .powered-by-skio {
    font-size: 0.8em;

    display: none;
    text-decoration: none;
    
    align-items: center;
    gap: 3px;
  }

  .skio-additional-info {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 2px;
    margin-bottom: 8px;
    padding-left: 30px;
  }

  .skio-additional-info img {
    width: 70px;
  }

  .skio-additional-info .skio-additional-content {
    width: 100%;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, max-content);
    row-gap: 8px;
    column-gap: 12px;
  }

  .skio-additional-info .skio-additional-item {
    display: flex;
    gap: 6px;
    align-items: center;
    font-size: 13px;
    line-height: 14.3px;
    color: var(--skio-text-color);
    margin: 0;
  }

  .skio-additional-info .skio-additional-item svg path {
    fill: var(--skio-text-color);
  }

  .skio-group-purpose {
    display: block;
    font-size: 14px;
    line-height: 1.3;
    color: var(--skio-text-color);
    margin-bottom: 4px;
  }

  .skio-group-purpose.skio-onetime-purpose {
    padding-top: 2px;
    padding-left: 30px;
    margin-bottom: 8px;
  }

  .skio-clone-group-content .skio-clone-group-content-title {
    font-size: 14px;
    line-height: 16.7px;
    color: #005648;
  }

  .skio-clone-group-content .skio-clone-frequency {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 11px;
    margin-top: 20px;
  }

  .skio-clone-group-content .skio-clone-frequency.skio-frequency--one {
    background-image: none;
    pointer-events: none;
  }

  .skio-clone-group-content .skio-clone-plan-id {
    position: relative;
    height: 49px;
    border-radius: 2px;
    border: 2px solid rgb(247, 245, 242);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    line-height: 16.7px;
    color: #000000;
  }

  .skio-clone-group-content .skio-clone-plan-id.first-plan {
    color: #004C3F;
  }

  .skio-clone-group-content .skio-clone-plan-id .skio-frequency-badge {
    position: absolute;
    top: -18px;
    right: -2px;
    padding: 4px 8px;
    border-top-right-radius: 2px;
    border-top-left-radius: 2px;
    background-color: rgb(247, 245, 242);
    font-size: 9px;
    line-height: 9.54px;
    color: #004C3F;
  }

  .skio-group-container--selected .skio-clone-group-content .skio-clone-plan-id.selected {
    border-color: #004C3F;
  }

  .skio-group-container--selected .skio-clone-group-content .skio-clone-plan-id.selected .skio-frequency-badge {
    color: #FFFFFF;
    background-color: #004C3F;
  }

  .skio-one-time-badge {
    position: absolute;
    top: -11px;
    right: 9px;
    font-size: 10px;
    line-height: 1;
    text-transform: uppercase;
    padding: 4px 5px;
    border-radius: 2px;
  }

  .skio-learn-more {
    width: fit-content;
    background: transparent;
    border: none;
    text-decoration: underline;
    cursor: pointer;
    margin: 0;
    font-family: var(--text-font-family);
    font-size: 12px;
    line-height: 1.25;
    color: var(--skio-text-color);
  }

  .skio-afterpay-content {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    margin: 3px 0;
    font-size: 12px;
    line-height: 1;
    color: var(--skio-text-color);
    padding-left: 30px;
  }

  .skio-afterpay-content span[afterpay-desc] {
    padding-right: 4px;
  }

  .skio-afterpay-content .skio-afterpay-with {
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
  }

  .skio-afterpay-content span[afterpay-price] {
    padding-right: 4px;
  }

  .skio-afterpay-content .skio-afterpay-with span[afterpay-desc] {
    padding-right: 0;
  }

  .skio-afterpay-content .skio-afterpay-logo svg {
    width: 62px;
    height: 15px;
    padding-left: 0px;
    padding-top: 4px;
  }

  .skio-afterpay-content .skio-afterpay-logo svg path {
    fill: var(--skio-text-color);
  }

  .skio-group-container--selected .skio-afterpay-content {
    color: var(--skio-selected-text-color);
  }

  .skio-group-container--selected .skio-afterpay-content .skio-afterpay-logo svg path {
    fill: var(--skio-selected-text-color);
  }

  @media screen and (min-width: 741px) {
    .hidden-tablet-and-up {
      display: none !important;
    }
  }

  @media (max-width: 740px) {
    .skio-group-title {
      font-size: 16px;
    }

    .skio-price {
      font-size: 15px;
      line-height: 16px;
    }

    .skio-price del {
      font-size: 12px;
      line-height: 16px;
      margin-right: 2px;
    }

    .skio-additional-info {
      gap: 4px;
      justify-content: center;
      margin: 2px 0 12px;
    }

    .skio-additional-info img {
      width: 84px;
    }

    .skio-additional-info .skio-additional-content {
      row-gap: 4px;
      grid-template-columns: repeat(1, 1fr);
    }

    .skio-additional-info .skio-additional-item {
      font-size: 12px;
      gap: 4px;
    }

    .skio-group-purpose {
      font-size: 13px;
      margin-top: 2px;
      margin-bottom: 8px;
    }

    .skio-clone-group-content .skio-clone-frequency {
      grid-template-areas:
          'header header'
          'left right';
      grid-template-columns: unset;
      gap: 8px;
      margin-top: 12px;
    }

    .skio-clone-group-content .skio-clone-plan-id.first-plan {
      grid-area: header;
    }

    .skio-group-label {
      padding: 10px 8px;
    }

    .skio-radio__container {
      margin-right: 8px;
    }

    .skio-additional-info,
    .skio-group-purpose.skio-onetime-purpose {
      padding-left: 28px;
    }

    .skio-afterpay-content {
      margin: 0;
      padding-top: 4px;
      padding-left: 28px;
      font-size: 11px;
    }

    .skio-afterpay-content span[afterpay-desc],
    .skio-afterpay-content span[afterpay-price] {
      padding-right: 3px;
    }

    .skio-afterpay-content .skio-afterpay-logo svg {
      width: 54px;
      height: 13px;
    }

    .hidden-phone {
      display: none !important;
    }
  }

  @media (max-width: 374px) {
    .skio-afterpay-content {
      font-size: 10.5px;
    }

    .skio-afterpay-content .skio-afterpay-logo svg {
      width: 50px;
      height: 12px;
    }
  }

`;

export class SkioPlanPickerComponent extends LitElement {
  static properties = {
    product: { type: Object },            //required
    productHandle: { type: String },      //optional (unless product isn't passed, then required)
    key: { type: String },                //optional, defaults to product.id; identifier for this instance of the Skio plan picker
    
    formId: { type: String },             //optional; if passed, used to connect input fields to form
    needsFormId: { type: Boolean },       //optional, defaults to false; if true, element needs to be passed a formId, else it searches for a form

    subscriptionFirst: { type: Boolean }, //optional, defaults to false; if true, shows subscription option above onetime
    startSubscription: { type: Boolean }, //optional, defaults to false; if true, auto-selects subscription on page load
    discountFormat: { type: String },     //optional, defaults to percent; can also pass "fixed"
    
    currency: { type: String },           //optional, defaults to 'USD', but can pass any 3 char identifier
    language: { type: String },           //optional, defaults to 'en-US', but can pass any similarly formatted language identifier
    moneyFormatter: {},                   //placeholder for object

    externalPriceSelector: { type: String },      //optional, used to update the external price

    externalPriceSelectorWithCurrency: { type: String },      //optional, used to update the external price
  
    selectedVariant: { type: Object },    //placeholder for data
    skioSellingPlanGroups: {},            //placeholder for data
    availableSellingPlanGroups: {},       //placeholder for data
    selectedSellingPlanGroup: {},         //placeholder for data
    selectedSellingPlan: {},              //placeholder for data

    defaultFrequency: {},                 //placeholder for data

    showAddToCartButton: { type: Boolean },

    meta: {},

    useVariantInputClickEvents: {type: Boolean}, // optional, allows use of variant input click events to update skio's selectedVariant
    variantInputSelector: {}
  };

  static styles = skioStyles;

  constructor() {
    super();
    this.product = null;
    this.productSubscriptionBenefits = null;
    this.selectedVariant = null;

    this.productHandle = null;

    this.purchaseOption = 'onetime';

    this.key = null;
    this.formId = null;
    this.needsFormId = false;

    this.skioSellingPlanGroups = [];
    this.availableSellingPlanGroups = [];

    this.selectedSellingPlanGroup = null;
    this.selectedSellingPlan = null;

    this.startSubscription = false;
    this.subscriptionFirst = false;

    this.skioMainProduct = true;

    this.discountFormat = 'percent';

    this.externalPriceSelector = '[skio-external-price]';

    this.externalPriceSelectorWithCurrency = '.product-meta__price-list-container .price-list .price';

    this.currency = Shopify.currency.active;
    this.language = 'en-US';
    this.moneyFormatter = new Intl.NumberFormat(this.language, {
      style: 'currency',
      currency: this.currency,
    });

    this.defaultFrequency = null;

    this.showAddToCartButton = false;

    this.meta = '';

    this.lastSellingPlanName = '';

    this.showDetailsHover = false;

    this.useVariantInputClickEvents = null;
    this.variantInputSelector = null;

    this.productCostPerServe = null;
    this.productOneTimeBadge = null;
    this.productOneTimeSaleDiscount = null;

  }

  connectedCallback() {
    super.connectedCallback();

    if (this.startSubscription == true) {
      this.purchaseOption = 'subscription';
    }

    if(!this.product && this.productHandle) {
      this.fetchProduct(this.productHandle);
    }

    if (this.needsFormId && this.formId == null) {
      let forms = document.querySelectorAll('form[action="/cart/add"]');
      if (forms.length > 0) {
        let form;
        forms.forEach((el) => {
          if (el.hasAttribute('skio-key')) {
            if (el.getAttribute('skio-key') == this.key) form = el;
          }
        });
        if (!form) form = forms[0];
        this.formId = form.id;
        this.requestUpdate();
      }
    }
    let skio = this;
    document.addEventListener("variantChanged", function(e) {
      //update variant id
      let variantId = e.detail.variantId;
      let variant = skio.product.variants.find(x => x.id == variantId);
      if (variant) skio.selectedVariant = variant;
      else skio.log("Unable to find variant with id: ", variantId);
      skio.requestUpdate();
    });

    this.moneyFormatter = new Intl.NumberFormat(this.language, {
      style: 'currency',
      currency: skio.currency,
    });

    if (this.meta !== '') this.meta = JSON.parse(this.meta);

    if (this.useVariantInputClickEvents) {
      document.addEventListener('load', skio.addVariantClickEventListeners)
    }

    if (!this.productSubscriptionBenefits) {
      let subscirptionBenetits = document.querySelector('[data-subscription-benefits-json]');
      if (subscirptionBenetits) {
        this.productSubscriptionBenefits = JSON.parse(subscirptionBenetits.innerHTML);
      }
    }

    if (!this.productCostPerServe) {
      this.productCostPerServe = document.querySelector('.product-grid-info_cost-per-serve');
    }

    if (!this.productOneTimeBadge) {
      let productOneTimeBadge = document.querySelector('[data-one-time-badge-json]');
      if (productOneTimeBadge) {
        this.productOneTimeBadge = JSON.parse(productOneTimeBadge.innerHTML);
      }
    }

    if (!this.productOneTimeSaleDiscount) {
      let productOneTimeSaleDiscount = document.querySelector('[data-one-time-sale-discount-json]');
      if (productOneTimeSaleDiscount) {
        this.productOneTimeSaleDiscount = JSON.parse(productOneTimeSaleDiscount.innerHTML);
      }
    }
  }

  render() {
    if(!this.product || !this.selectedVariant || this.skioSellingPlanGroups.length == 0 || !this.product?.available) return;
    
    return html`
      <fieldset class="skio-plan-picker" skio-plan-picker="${ this.key }">
        <input ${ this.formId !== null ? html`form="${ this.formId }"` : '' } name="selling_plan" type="hidden" value="${ this.selectedSellingPlan !== null ? this.selectedSellingPlan?.id : ''}" />
        <input ${ this.formId !== null ? html`form="${ this.formId }"` : '' } name="properties[Discount]" type="hidden" value="${ this.selectedSellingPlan !== null ? this.discount(this.selectedSellingPlan).percent : '' }" 
          ?disabled="${ this.selectedSellingPlan == null ? true : false }" />
        
        <div class="skio-group-container ${ this.product.requires_selling_plan == false ? 'skio-group-container--available' : '' } ${ this.selectedSellingPlanGroup == null ? 'skio-group-container--selected' : '' } ${ this.subscriptionFirst ? 'skio-onetime-second' : ''}" skio-group-container 
          @click=${(e) => this.selectSellingPlanGroup(null, e) } >
        
          <input id="skio-one-time-${ this.key }" class="skio-group-input" name="skio-group-${ this.key }" type="radio" value="" 
            skio-one-time ?checked=${ this.startSubscription == false && this.product.requires_selling_plan == false ? true : false }>
          <label class="skio-group-label" for="skio-one-time-${ this.key }">
            <div class="skio-group-topline">
              <div class="skio-radio__container">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"></circle>
                  <circle class="skio-radio" cx="12" cy="12" r="7" fill="#005648"></circle>
                </svg>
              </div>
              <div class="skio-group-title">
                One-time
              </div>
              <div class="skio-price">
                <span skio-onetime-price>${ this.productOneTimeSaleDiscount ? html`
                    <del>${ this.moneyFormatter.format(this.selectedVariant.price / 100) }</del>
                    ${ this.productOneTimeSaleDiscount.price }
                  ` : this.moneyFormatter.format(this.selectedVariant.price / 100) }</span>
              </div>
            </div>
            <span class="skio-group-purpose skio-onetime-purpose">Designed as a starting point rather than a full results phase</span>
            <p class="skio-afterpay-content">
              <span afterpay-desc>Make 4 interest-free payments of</span>
              <span afterpay-price>${ this.moneyFormatter.format(this.selectedVariant.price / 4 / 100) }</span>
              <span class="skio-afterpay-with"><span afterpay-desc>with</span>
              <span class="skio-afterpay-logo">
                <svg width="92" height="24" viewBox="0 0 92 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M86.7667 7.29549L84.2443 5.84977L81.7219 4.33295C81.3346 4.11298 80.896 3.99818 80.4502 4.00002C80.0043 4.00187 79.5667 4.1203 79.1812 4.34346C78.7957 4.56663 78.4758 4.88671 78.2535 5.27167C78.0312 5.65664 77.9143 6.09299 77.9145 6.53708V6.84519C77.9144 6.93386 77.936 7.02122 77.9775 7.09969C78.019 7.17816 78.079 7.24537 78.1525 7.29549L79.3423 7.9591C79.3981 7.99217 79.46 8.01354 79.5244 8.02188C79.5888 8.03022 79.6542 8.02537 79.7166 8.00761C79.779 7.98985 79.8371 7.95956 79.8873 7.91862C79.9375 7.87767 79.9788 7.82692 80.0086 7.7695C80.0298 7.69191 80.0298 7.61008 80.0086 7.53249V6.77408C80.0054 6.70871 80.0152 6.64334 80.0374 6.58173C80.0596 6.52013 80.0937 6.46349 80.138 6.41505C80.1822 6.36662 80.2355 6.32735 80.295 6.29949C80.3545 6.27162 80.4189 6.25572 80.4845 6.25268H80.7701L83.1497 7.60359L85.5293 8.93081C85.645 9.00401 85.7273 9.11949 85.7584 9.25243C85.7896 9.38538 85.7671 9.52518 85.6959 9.64182L85.5293 9.80772L83.1497 11.1349L80.7701 12.4859C80.7139 12.5204 80.6514 12.5435 80.5863 12.5539C80.5211 12.5642 80.4545 12.5616 80.3903 12.5462C80.3261 12.5307 80.2657 12.5028 80.2124 12.464C80.1592 12.4252 80.1141 12.3762 80.08 12.32C80.0558 12.2267 80.0558 12.1288 80.08 12.0356V11.6563C80.0802 11.2123 79.9633 10.7759 79.741 10.3909C79.5187 10.006 79.1988 9.68589 78.8133 9.46273C78.4278 9.23956 77.9902 9.12113 77.5444 9.11929C77.0985 9.11744 76.6599 9.23225 76.2726 9.45222L73.7026 10.9216L71.1802 12.3674C70.5978 12.7047 70.1717 13.2564 69.9937 13.9036C69.8157 14.5508 69.8999 15.2417 70.2283 15.8276C70.4474 16.2283 70.7779 16.5574 71.1802 16.7756L73.7026 18.2213L76.2726 19.667C76.6599 19.887 77.0985 20.0018 77.5444 20C77.9902 19.9981 78.4278 19.8797 78.8133 19.6565C79.1988 19.4334 79.5187 19.1133 79.741 18.7283C79.9633 18.3434 80.0802 17.907 80.08 17.4629V17.1548C80.0801 17.0661 80.0585 16.9788 80.017 16.9003C79.9755 16.8218 79.9155 16.7546 79.842 16.7045L78.6522 16.0172C78.5964 15.9841 78.5345 15.9628 78.4701 15.9544C78.4057 15.9461 78.3404 15.9509 78.2779 15.9687C78.2155 15.9865 78.1574 16.0167 78.1072 16.0577C78.057 16.0986 78.0157 16.1494 77.9859 16.2068C77.9647 16.2924 77.9647 16.3819 77.9859 16.4675V17.2259C77.9891 17.2913 77.9793 17.3567 77.9571 17.4183C77.9349 17.4799 77.9008 17.5365 77.8566 17.5849C77.8123 17.6334 77.759 17.6727 77.6995 17.7005C77.64 17.7284 77.5756 17.7443 77.51 17.7473C77.416 17.7687 77.3184 17.7687 77.2244 17.7473L74.8448 16.3964L72.4652 15.0692C72.3451 15.0124 72.2512 14.9122 72.2026 14.7889C72.154 14.6657 72.1543 14.5286 72.2034 14.4056L72.37 14.2397L74.7496 12.9125L77.1292 11.5615C77.1854 11.527 77.2479 11.5039 77.3131 11.4936C77.3783 11.4832 77.4449 11.4858 77.509 11.5012C77.5732 11.5167 77.6336 11.5446 77.6869 11.5834C77.7402 11.6222 77.7852 11.6712 77.8193 11.7274C77.8435 11.8207 77.8435 11.9186 77.8193 12.0119V12.3911C77.8191 12.8351 77.936 13.2715 78.1583 13.6565C78.3806 14.0414 78.7005 14.3615 79.086 14.5847C79.4715 14.8078 79.9091 14.9263 80.355 14.9281C80.8008 14.93 81.2394 14.8152 81.6267 14.5952L84.1967 13.1258L86.7191 11.68C87.0036 11.5057 87.2505 11.2769 87.4455 11.0067C87.6404 10.7366 87.7796 10.4306 87.8548 10.1065C87.9301 9.78239 87.94 9.44662 87.8839 9.11867C87.8279 8.79073 87.7069 8.47714 87.5282 8.1961C87.3545 7.83688 87.0926 7.52712 86.7667 7.29549Z" fill="black"/>
                  <path d="M70.1569 7.72192L64.2316 19.9039H61.852L64.0651 15.3534L60.5908 7.72192H63.1132L65.3501 12.8175L67.7297 7.72192H70.1569Z" fill="black"/>
                  <path d="M10.928 12.0355C10.9048 11.5725 10.7457 11.1264 10.4705 10.7526C10.1952 10.3787 9.81598 10.0935 9.37963 9.93213C8.94328 9.77078 8.469 9.74042 8.0155 9.84482C7.56201 9.94921 7.14921 10.1838 6.82821 10.5195C6.50721 10.8552 6.2921 11.2772 6.20952 11.7335C6.12693 12.1897 6.1805 12.66 6.36358 13.0862C6.54666 13.5124 6.85122 13.8758 7.23956 14.1313C7.6279 14.3869 8.08296 14.5234 8.54841 14.524C9.17953 14.524 9.78479 14.2743 10.2311 13.8298C10.6773 13.3854 10.928 12.7825 10.928 12.154V12.0355ZM10.928 16.3726V15.235C10.5923 15.6391 10.1703 15.9637 9.69281 16.1851C9.21533 16.4064 8.69433 16.5191 8.16767 16.5148C7.60826 16.5025 7.05676 16.3805 6.54469 16.1559C6.03262 15.9312 5.57001 15.6083 5.18329 15.2055C4.79656 14.8028 4.49331 14.3281 4.29085 13.8085C4.08839 13.289 3.99068 12.7348 4.00332 12.1777V12.0355C3.98102 11.4689 4.07109 10.9035 4.26837 10.3717C4.46566 9.83983 4.76629 9.35194 5.15306 8.9359C5.53984 8.51987 6.00517 8.18386 6.52245 7.9471C7.03972 7.71035 7.59879 7.57748 8.16767 7.55611C8.68497 7.55267 9.19659 7.66356 9.66568 7.88076C10.1348 8.09797 10.5497 8.4161 10.8804 8.81223V7.72201H12.9983V16.3726H10.928Z" fill="black"/>
                  <path d="M23.4695 14.4054C22.708 14.4054 22.5176 14.121 22.5176 13.41V9.66534H23.874V7.79302H22.5176V5.61259H20.3284V7.72192H17.4728V6.84501C17.4728 6.134 17.7584 5.84959 18.5199 5.84959H18.9958V4.16687H17.9725C16.164 4.16687 15.3074 4.75938 15.3074 6.5369V7.69822H14.0938V9.66534H15.3074V16.4436H17.4728V9.66534H20.3284V13.9077C20.3284 15.6852 20.9947 16.4436 22.708 16.4436H23.8978V14.4054H23.4695Z" fill="black"/>
                  <path d="M31.3219 11.2534C31.251 10.7404 30.9888 10.2728 30.5876 9.94349C30.1864 9.61416 29.6755 9.44723 29.1564 9.47588C28.6358 9.45201 28.1247 9.62013 27.7208 9.94807C27.3169 10.276 27.0486 10.7408 26.9672 11.2534H31.3219ZM26.991 12.6043C27.0277 13.1638 27.2818 13.6871 27.6994 14.0631C28.117 14.4392 28.6652 14.6385 29.2278 14.6189C29.6362 14.6237 30.039 14.5238 30.3974 14.3287C30.7559 14.1336 31.0578 13.8499 31.2743 13.5049H33.5111C33.2344 14.4187 32.6564 15.2129 31.8705 15.7591C31.0847 16.3052 30.1368 16.5715 29.1802 16.5149C28.0475 16.593 26.9299 16.22 26.0732 15.4778C25.2164 14.7357 24.6906 13.6851 24.6113 12.5569V12.0355C24.6113 10.8475 25.0852 9.70817 25.9286 8.86813C26.7721 8.02809 27.916 7.55615 29.1088 7.55615C30.3016 7.55615 31.4456 8.02809 32.289 8.86813C33.1325 9.70817 33.6063 10.8475 33.6063 12.0355C33.63 12.2165 33.63 12.3997 33.6063 12.5806L26.991 12.6043Z" fill="black"/>
                  <path d="M47.5978 12.0355C47.5822 11.4123 47.3206 10.8203 46.8697 10.3878C46.4189 9.95527 45.8151 9.71712 45.1892 9.72491C44.5633 9.7327 43.9657 9.98581 43.5258 10.4294C43.086 10.873 42.8393 11.4713 42.8393 12.0948C42.8393 12.7182 43.086 13.3165 43.5258 13.7601C43.9657 14.2037 44.5633 14.4568 45.1892 14.4646C45.8151 14.4724 46.4189 14.2342 46.8697 13.8017C47.3206 13.3693 47.5822 12.7773 47.5978 12.154V12.0355ZM40.7207 19.904V7.72205H42.8386V8.83596C43.1744 8.43184 43.5963 8.1073 44.0738 7.88592C44.5513 7.66454 45.0723 7.55189 45.5989 7.55615C46.1583 7.56541 46.7104 7.68433 47.2237 7.90613C47.7369 8.12793 48.2012 8.44825 48.5902 8.84881C48.9791 9.24936 49.285 9.72231 49.4903 10.2406C49.6957 10.7589 49.7965 11.3125 49.7871 11.8696V12.0355C49.8094 12.6021 49.7193 13.1674 49.522 13.6993C49.3248 14.2311 49.0241 14.719 48.6374 15.1351C48.2506 15.5511 47.7852 15.8871 47.268 16.1239C46.7507 16.3606 46.1916 16.4935 45.6227 16.5149C45.1241 16.5244 44.6295 16.4254 44.1732 16.2248C43.717 16.0242 43.3103 15.7269 42.9813 15.3535V19.904H40.7207Z" fill="black"/>
                  <path d="M57.4974 12.0355C57.4738 11.5777 57.3174 11.1365 57.0471 10.7655C56.7769 10.3944 56.4044 10.1094 55.9748 9.9449C55.5452 9.78041 55.0769 9.74353 54.6267 9.83872C54.1765 9.93391 53.7637 10.1571 53.4382 10.4812C53.1128 10.8053 52.8887 11.2165 52.7931 11.6649C52.6976 12.1133 52.7346 12.5797 52.8997 13.0076C53.0649 13.4354 53.3511 13.8064 53.7237 14.0755C54.0962 14.3447 54.5391 14.5005 54.9988 14.524H55.1178C55.7489 14.524 56.3541 14.2743 56.8004 13.8298C57.2467 13.3854 57.4974 12.7825 57.4974 12.154V12.0355ZM57.4974 16.3726V15.235C57.1675 15.6399 56.7501 15.9653 56.2762 16.1869C55.8023 16.4085 55.2843 16.5206 54.7608 16.5148C54.2014 16.5056 53.6493 16.3866 53.1361 16.1648C52.6229 15.943 52.1585 15.6227 51.7696 15.2222C51.3807 14.8216 51.0748 14.3487 50.8694 13.8303C50.664 13.312 50.5632 12.7585 50.5727 12.2014V12.0355C50.5504 11.4689 50.6404 10.9035 50.8377 10.3717C51.035 9.83983 51.3356 9.35193 51.7224 8.93589C52.1092 8.51986 52.5745 8.18385 53.0918 7.9471C53.6091 7.71034 54.1681 7.57747 54.737 7.5561C55.2508 7.55285 55.7589 7.66394 56.2241 7.88126C56.6894 8.09858 57.1 8.41664 57.426 8.81222V7.722H59.5677V16.3726H57.4974Z" fill="black"/>
                  <path d="M36.8663 8.57523C37.0641 8.25771 37.3414 7.99684 37.671 7.81818C38.0005 7.63953 38.371 7.54922 38.7462 7.55612C39.066 7.54788 39.3835 7.61274 39.6742 7.74572V9.97355C39.2104 9.6889 38.6718 9.5486 38.1275 9.57064C37.7531 9.65076 37.4227 9.86842 37.2022 10.1803C36.9817 10.4921 36.8872 10.8753 36.9377 11.2534V16.3726H34.7246V7.72202H36.8663V8.57523Z" fill="black"/>
                </svg>
              </span></span>
            </p>
          </label>
          ${ this.productOneTimeBadge && html`
              <div class="skio-one-time-badge" style="color: ${ this.productOneTimeBadge.color }; background-color: ${ this.productOneTimeBadge.bgcolor };">
                ${ this.productOneTimeBadge.title }
              </div>
            ` }
        </div>
        ${ this.availableSellingPlanGroups ? this.availableSellingPlanGroups.map((group, index) => 
          html`
            <div class="skio-group-container skio-group-container--available ${ this.selectedSellingPlanGroup == group ? 'skio-group-container--selected' : '' }" skio-group-container
              @click=${(e) => this.selectSellingPlanGroup(group, e) }>
              <input id="skio-selling-plan-group-${ index }-${ this.key }" class="skio-group-input" name="skio-group-${ this.key }"
                type="radio" value="${ group.id }" skio-selling-plan-group="${ group.id }" ?checked=${ this.selectedSellingPlanGroup == group ? true : false } >
              <label class="skio-group-label" for="skio-selling-plan-group-${ index }-${ this.key }">
                <div class="skio-group-topline">
                  <div class="skio-radio__container">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"></circle>
                      <circle class="skio-radio" cx="12" cy="12" r="7" fill="#005648"></circle>
                    </svg>
                  </div>
                  <div class="skio-group-title">
                  ${ group.name == 'Subscription' ? 'Subscribe & Save' : group.name }
                    ${ this.discount(group.selected_selling_plan).percent !== '0%' ? html`
                      <span> <span skio-discount>${ this.discountFormat == 'percent' ? this.discount(group.selected_selling_plan).percent : this.discount(group.selected_selling_plan).amount }</span></span>
                    ` : html`` }
                  </div>
                  <div class="skio-price">
                    ${ this.discount(group.selected_selling_plan).amount > 0 ? html`
                      <del>${ this.moneyFormatter.format(this.selectedVariant.price / 100) }</del>
                    ` : html`` }
                    <span skio-subscription-price>${ this.price(group.selected_selling_plan) }</span>
                  </div>
                </div>

                ${ this.productSubscriptionBenefits ? html`
                  <div class="skio-additional-info">
                    <span class="skio-group-purpose">To support meaningful progress that lasts</span>
                    ${ this.productSubscriptionBenefits.benefits ? html`
                      <div class="skio-additional-content">
                        ${ this.productSubscriptionBenefits.benefits.map((benefit) => 
                          html`
                            <div class="skio-additional-item">
                              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12.9695 4.43697L5.9695 11.437C5.92887 11.4776 5.88062 11.5099 5.82751 11.5319C5.7744 11.554 5.71747 11.5653 5.65997 11.5653C5.60248 11.5653 5.54555 11.554 5.49244 11.5319C5.43932 11.5099 5.39107 11.4776 5.35044 11.437L2.28794 8.37447C2.20585 8.29238 2.15973 8.18104 2.15973 8.06494C2.15973 7.94884 2.20585 7.8375 2.28794 7.75541C2.37003 7.67332 2.48138 7.6272 2.59747 7.6272C2.71357 7.6272 2.82491 7.67332 2.907 7.75541L5.65997 10.5089L12.3504 3.81791C12.4325 3.73582 12.5439 3.6897 12.66 3.6897C12.7761 3.6897 12.8874 3.73582 12.9695 3.81791C13.0516 3.9 13.0977 4.01134 13.0977 4.12744C13.0977 4.24354 13.0516 4.35488 12.9695 4.43697Z" fill="#004C3F"/>
                              </svg>
                              <span class="skio-additional-item-text">${ benefit }</span>
                            </div>
                          `
                        ) }
                      </div>
                    ` : `` }
                    <button is="toggle-button" class="skio-learn-more product-meta__help link text--subdued hidden-tablet-and-up" aria-controls="product-skio-learn-more-help-popover" aria-expanded="false">Learn more</button>
                    <button is="toggle-button" class="skio-learn-more product-meta__help link text--subdued hidden-phone" aria-controls="product-skio-learn-more-help-drawer" aria-expanded="false">Learn more</button>
                  </div>
                 ` : '' }

                <div class="skio-group-content">
                  <select skio-selling-plans="${ group.id }" class="skio-frequency${ group.selling_plans.length == 1 ? ' skio-frequency--one' : '' }"
                    @change=${ (e) => this.selectSellingPlan(e.target, group) }>
                    ${ group ? group.selling_plans.map((selling_plan) => 
                      html`
                      <option value="${ selling_plan.id }" ?selected=${group.selected_selling_plan == selling_plan }>
                        ${ group.name == 'Subscription' ? `Delivery ${ selling_plan.name.toLowerCase() }` : `${ selling_plan.name }` }
                      </option>
                      `
                    ): ''}
                  </select>
                </div>
              </label>
            </div>
          `
        ): ''}

            ${ this.showAddToCartButton ? html`
              <button @click=${() => this.addToCart() } class="add-to-cart">Add to Cart</button>
            ` : html``}

      </fieldset>`
  }

  updated = (changed) => {
    if(changed.has('product') && this.product) {
      //update key
      this.key = this.key ? this.key : this.product.id;

      //update skioSellingPlanGroups
      this.skioSellingPlanGroups = this.product.selling_plan_groups.filter(
        selling_plan_group => selling_plan_group.app_id === 'SKIO' && !selling_plan_group.name.toLowerCase().includes('hidden-') && !selling_plan_group.name.toLowerCase().includes('dynamic box subscription')
      )

      this.skioSellingPlanGroups.forEach((group) => {
        group.selling_plans.sort(function(a,b){
          if (parseInt(a.name.replace(/\D/g, "")) < parseInt(b.name.replace(/\D/g, ""))) return -1;
          if (parseInt(a.name.replace(/\D/g, "")) > parseInt(b.name.replace(/\D/g, ""))) return 1;
          if (parseInt(a.name.replace(/\D/g, "")) == parseInt(b.name.replace(/\D/g, ""))) return 0;
        })
      });

    }

    if(changed.has('selectedVariant') && this.selectedVariant) {
      //update availableSellingPlanGroups based on skioSellingPlanGroups and selectedVariant.id
      let skioSellingPlanGroups = JSON.parse(JSON.stringify(this.skioSellingPlanGroups));
      this.availableSellingPlanGroups = skioSellingPlanGroups.filter(selling_plan_group =>
        selling_plan_group.selling_plans.some(selling_plan =>
          this.selectedVariant.selling_plan_allocations.some(
            selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id
          )
        )
      )

      //update selectedSellingPlan value
      if (this.availableSellingPlanGroups?.length > 0) {
        //update each group with a default selected_selling_plan

        this.availableSellingPlanGroups.forEach((group) => {
          group.selling_plans = group.selling_plans.filter(x => this.selectedVariant.selling_plan_allocations.find(y => y.selling_plan_id == x.id) );
        })

        this.availableSellingPlanGroups.forEach((group => {
          if (this.defaultFrequency) {
            let selling_plan = group.selling_plans.find(x => x.name.toLowerCase().includes(this.defaultFrequency.toLowerCase()));
            if (selling_plan) group.selected_selling_plan = selling_plan;
            else group.selected_selling_plan = group.selling_plans[0];
          } else {
            group.selected_selling_plan = group.selling_plans[0];
          }
       }));

        if (this.startSubscription == true || this.product.requires_selling_plan == true || this.purchaseOption == 'subscription') {
          //find a matching selling plan, or choose first available

          if (this.selectedSellingPlan == null || this.selectedSellingPlan == undefined) {
            this.selectSellingPlanGroup(this.availableSellingPlanGroups[0], e);
          }

          let sellingPlanName = this.selectedSellingPlan?.name;

          let sellingPlanGroup = this.availableSellingPlanGroups.find(x => x.selling_plans.find(y => y.name == sellingPlanName));
          let sellingPlan = sellingPlanGroup?.selling_plans.find(y => y.name == sellingPlanName);
          if (sellingPlanName == sellingPlan.name) {
            this.selectedSellingPlanGroup = sellingPlanGroup;
            this.selectedSellingPlan = sellingPlan;
          } else {
          this.selectedSellingPlanGroup = this.availableSellingPlanGroups[0];
          this.selectedSellingPlan = this.availableSellingPlanGroups[0].selling_plans[0];
          }

        } else {
          this.selectedSellingPlan, this.selectedSellingPlanGroup = null
        }
      } else {
        this.selectedSellingPlan, this.selectedSellingPlanGroup = null
      }

      //update the form that was passed, if any
      this.updateForm();

    }

    if(changed.has('selectedSellingPlan')) {
      //update price of price elements if applicable
      document.querySelectorAll(`[skio-price][skio-key="${ this.key }"]`).forEach((el) => {
        el.innerHTML = this.price(this.selectedSellingPlan);
      });

      //update display of external content elements
      document.querySelectorAll(`[skio-onetime-content][skio-key="${ this.key }"]`).forEach((el) => {
        this.selectedSellingPlan !== null ? el.style.display = "none" : el.style.removeProperty('display');
      });

      document.querySelectorAll(`[skio-subscription-content][skio-key="${ this.key }"]`).forEach((el) => {
        this.selectedSellingPlan == null ? el.style.display = "none" : el.style.removeProperty('display');
      });

      //dispatch CustomEvent to tell that this specific plan picker was updated, and pass the selectedSellingPlan
      const event = new CustomEvent(`skio::update-selling-plan`, {
        bubbles: true, 
        composed: true, 
        detail: {
          sellingPlan: this.selectedSellingPlan,
          key: this.key
        }
      });

      this.dispatchEvent(event);

      //update the form that was passed, if any
      this.updateForm();
      this.updateExternalPrice();

      //update external selling_plan input value
      let sellingPlanInput = document.querySelectorAll('input[name="selling_plan"]')
      sellingPlanInput.forEach(input => {
        this.selectedSellingPlan != null ? input.value = this.selectedSellingPlan.id : input.value = ''
      })

    }

    if(changed.has('formId')) {
      //update the form that was passed, if any
      this.updateForm();
    }

  }

  log = (...args) => {
    args.unshift('%c[skio]', 'color: #8770f2;');
    console.log.apply(console, args);
  }

  error = (...args) =>  {
    args.unshift('%c [skio]', 'color: #ff0000');
    console.error.apply(console, args);
  }

  detailsMouseover() {
    let details = this.renderRoot.querySelector('.skio-details');
    let summary = this.renderRoot.querySelector('.skio-details summary');
    if (!details.hasAttribute('open') && this.showDetailsHover == false) {
      summary.click();
      this.showDetailsHover = true;
    }
  }

  detailsMouseleave() {
    let details = this.renderRoot.querySelector('.skio-details');
    let summary = this.renderRoot.querySelector('.skio-details summary');
    if (details.hasAttribute('open') && this.showDetailsHover == true) {
      summary.click();
      this.showDetailsHover = false;
    }
  }

  updatePriceElement() {
    let priceEl = document.querySelector(`[data-skio-price="${ this.product.id }"]`);
    if (priceEl) {
      //add formatted price here
      let high = 0;
      this.product.variants.forEach((variant) => {
        if (variant.price > high) high = variant.price;
      });
      let content = '';
      if (!this.selectedSellingPlan) {
        content = this.moneyFormatter.format(this.selectedVariant.price / 100).replaceAll('A', '');
      } else {
        content = `<del>${this.moneyFormatter.format(high / 100).replaceAll('A', '')}</del>${this.price(this.selectedSellingPlan).replaceAll('A', '')}`;
      }
      priceEl.innerHTML = content;
    }
  }

  getMetadata(field) {
    let meta = this.meta;
    if (meta !== {}){
      let fieldData = meta[field];
      if (fieldData) return fieldData;
      else return '';
    }
  }

  getVariantMetadata(variantId, field) {
    let meta = this.meta;
    if (meta !== {}){
      let variantData = meta[variantId];
      if (variantData) {
        let fieldData = variantData[field];
        if (fieldData) return fieldData;
        else return '';
      }
    }
  }

  updateExternalPrice() {
    document.querySelectorAll(this.externalPriceSelector).forEach((el) => {
      this.selectedSellingPlan ? el.innerHTML = this.price(this.selectedSellingPlan) : el.innerHTML = this.money( this.selectedVariant.price);
    })
    document.querySelectorAll(this.externalPriceSelectorWithCurrency).forEach((el) => {
      this.selectedSellingPlan ? el.innerHTML = this.price(this.selectedSellingPlan).replaceAll('A$', '$') + ' ' + this.currency : el.innerHTML = this.money( this.selectedVariant.price).replaceAll('A$', '$') + ' ' + this.currency;
    })
  }
  
  // Update selected selling plan group; called on click of skio-group-container element
  selectSellingPlanGroup(group, e) {
    if (group && e.target.classList.contains('skio-learn-more')) {
      return;
    }
    this.selectedSellingPlanGroup = group;
    this.selectedSellingPlan = group?.selected_selling_plan;
    if (this.selectedSellingPlan) this.lastSellingPlanName = this.selectedSellingPlan.name;
    if (group) {
      this.purchaseOption = 'subscription';
      document.querySelectorAll('.subnav-wrapper .add-to-cart-info__price span:first-of-type').forEach(span => {
        span.innerHTML = group.name;
      });
      document.querySelectorAll('.subnav-wrapper .add-to-cart-info__price span:last-of-type').forEach(span => {
        span.innerHTML = this.price(this.selectedSellingPlan);
      });
    } else {
      this.purchaseOption = 'onetime';
      document.querySelectorAll('.subnav-wrapper .add-to-cart-info__price span:first-of-type').forEach(span => {
        span.innerHTML = 'One-time';
      })
      document.querySelectorAll('.subnav-wrapper .add-to-cart-info__price span:last-of-type').forEach(span => {
        span.innerHTML = this.moneyFormatter.format(this.selectedVariant.price / 100);
      })
    }

    this.updateCostPerServe(group);

    //update the form that was passed, if any
    this.updateForm();
  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlan(element, group) {
    let selling_plan = group.selling_plans.find(x => x.id == element.value);
    if (selling_plan) {
      group.selected_selling_plan = selling_plan;
      this.selectedSellingPlanGroup = group;
      this.selectedSellingPlan = selling_plan;
      this.lastSellingPlanName = this.selectedSellingPlan.name;
    }
    else this.log("Error: couldn't find selling plan with id " + element.value + " for variant " + this.selectedVariant.id + " from product " + this.product.id + " : " + this.product.handle);
  }

  clickSellingPlan(selling_plan_id, group) {
    let selling_plan = group.selling_plans.find(x => x.id == selling_plan_id);
    if (selling_plan) {
      group.selected_selling_plan = selling_plan;
      this.selectedSellingPlanGroup = group;
      this.selectedSellingPlan = selling_plan;
      this.lastSellingPlanName = this.selectedSellingPlan.name;
      this.updateCostPerServe(group);
    }
    else this.log("Error: couldn't find selling plan with id " + selling_plan_id + " for variant " + this.selectedVariant.id + " from product " + this.product.id + " : " + this.product.handle);
  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlanButton(plan, group) {
    let selling_plan = group.selling_plans.find(x => x.id == plan.id);
    if (selling_plan) {
      group.selected_selling_plan = selling_plan;
      this.selectedSellingPlanGroup = group;
      this.selectedSellingPlan = selling_plan;
      this.lastSellingPlanName = this.selectedSellingPlan.name;
    }
    else this.log("Error: couldn't find selling plan with id " + element.value + " for variant " + this.selectedVariant.id + " from product " + this.product.id + " : " + this.product.handle);
  }

  // Formats integer value into money value
  money(price) {
    return this.moneyFormatter.format(price / 100.0)
  }

  // Calculates discount based on selling_plan.price_adjustments, returns { percent, amount } of selling plan discount
  discount(selling_plan) {

    if (!selling_plan)
      return { percent: '0%', amount: 0 }

    if (selling_plan.name.includes('prepaid')) {

      let option = selling_plan.options[0]['value'];

      var numberPattern = /\d+/g;
      let result = parseInt(option.match( numberPattern ).join(''));

      const price_adjustment = selling_plan.price_adjustments[0]
      const discount = { percent: '0%', amount: 0 }
      const price = this.selectedVariant.price * result;
      
      switch (price_adjustment.value_type) {
        case 'percentage':
          discount.percent = `${price_adjustment.value}%`
          discount.amount = Math.round(
            (price * price_adjustment.value) / 100.0
          )
          break
        case 'fixed_amount':
          discount.percent = `${Math.round(
            ((price_adjustment.value * 1.0) / price) * 100.0
          )}%`
          discount.amount = price - price_adjustment.value
          break
        case 'price':
          discount.percent = `${Math.round(
            (((price - price_adjustment.value) * 1.0) /
              price) *
              100.0
          )}%`
          discount.amount = price - price_adjustment.value
          break
      }
      
      return discount

    } else {

      const price_adjustment = selling_plan.price_adjustments[0]
      const discount = { percent: '0%', amount: 0 }
      const price = this.selectedVariant.price;
      
      switch (price_adjustment.value_type) {
        case 'percentage':
          discount.percent = `${price_adjustment.value}%`
          discount.amount = Math.round(
            (price * price_adjustment.value) / 100.0
          )
          break
        case 'fixed_amount':
          discount.percent = `${Math.round(
            ((price_adjustment.value * 1.0) / price) * 100.0
          )}%`
          discount.amount = price_adjustment.value
          break
        case 'price':
          discount.percent = `${Math.round(
            (((price - price_adjustment.value) * 1.0) /
              price) *
              100.0
          )}%`
          discount.amount = price - price_adjustment.value
          break
      }
      
      return discount

    }
    
  }

  // Calculates the variant's price for the given selling plan, returns a formatted money value (if desired)
  price(selling_plan, formatted = true) {

    if (selling_plan.name.includes('prepaid')) {

      let option = selling_plan.options[0]['value'];
      var numberPattern = /\d+/g;
      let result = parseInt(option.match( numberPattern ).join(''));

      return formatted
        ? this.money( (this.selectedVariant.price * result) - this.discount(selling_plan).amount)
        :  (this.selectedVariant.price * result) - this.discount(selling_plan).amount

    } else {

      return formatted
        ? this.money( this.selectedVariant.price - this.discount(selling_plan).amount)
        :  this.selectedVariant.price - this.discount(selling_plan).amount

    }

    
  }

  // If a formId was passed, appends the necessary <input> elements to the form
  updateForm() {
    if (this.formId) {
      let form = document.querySelector(`#${this.formId}`);

      if (form) {
        let selling_plan_input = form.querySelector('[name="selling_plan"]');
        if (selling_plan_input) {
          selling_plan_input.value = this.selectedSellingPlan?.id !== undefined ? this.selectedSellingPlan?.id : null;
          selling_plan_input.disabled = this.selectedSellingPlan?.id !== undefined ? false : true;
        } else {
          selling_plan_input = document.createElement('input');
          selling_plan_input.type = "hidden";
          selling_plan_input.name = "selling_plan";
          selling_plan_input.value = this.selectedSellingPlan?.id !== undefined ? this.selectedSellingPlan?.id : null;
          selling_plan_input.disabled = this.selectedSellingPlan?.id !== undefined ? false : true;
          form.append(selling_plan_input);
        }

        let discountValue = this.selectedSellingPlan?.id !== undefined ? this.discount(this.selectedSellingPlan).percent : null;
        if (discountValue == '0%') discountValue = null;

        let discount_input = form.querySelector('[name="properties[Discount]"]');
        if (discount_input) {
          discount_input.value = this.selectedSellingPlan?.id !== undefined ? this.discount(this.selectedSellingPlan).percent : null;
          discount_input.disabled = this.selectedSellingPlan?.id !== undefined ? false : true;
          if (discountValue == null) discount_input.disabled = true;
        } else {
          discount_input = document.createElement('input');
          discount_input.type = "hidden";
          discount_input.name = "properties[Discount]";
          discount_input.value = this.selectedSellingPlan?.id !== undefined ? this.discount(this.selectedSellingPlan).percent : null;
          discount_input.disabled = this.selectedSellingPlan?.id !== undefined ? false : true;
          if (discountValue == null) discount_input.disabled = true;
          form.append(discount_input);
        }

      } else {
        console.log(`Skio error: form ID is ${ this.formId }, but no form with that ID was found.`);
      }
    }
  }

  addVariantClickEventListeners() {
    let variantInputs = document.querySelectorAll(this.variantInputSelector)
    let skio = this
    for (let el of variantInputs) {
      el.addEventListener('click', function(e) {
        // may need to replace with ID / e.target depending on client setup
        // may need to use different attribute depending on ^^
        let variantTitle = e.currentTarget.value
        skio.selectedVariant = skio.product.variants.find(variant => variant.title == variantTitle)
      })
    }
  }

  // Optional functions keep if necessary 

  /**
   *   
   * 
   */
  
  // Runs a fetch request to add the selectedVariant to the cart with the passed quantity and selectedSellingPlan
  addToCart(quantity = 1) {
    const items = [
      {
        id: this.selectedVariant.id,
        quantity: quantity,
        ...(this.selectedSellingPlan && { selling_plan: this.selectedSellingPlan?.id })
      }
    ];

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ items })
    })
    .then((response) => response.json())
    .then((response) => {
      this.log("SKIO added item to cart: ", response);
      //dispatch CustomEvent to tell document that an item was added to cart
      const event = new CustomEvent(`skio::added-to-cart`, {
        bubbles: true, 
        composed: true, 
        detail: {
          response,
          key: this.key
        }
      });

      this.dispatchEvent(event);
    })
    .catch((error) => {
      this.error(`SKIO ${ this.key } error adding item to cart: `, error);
    });
  } 

  fetchProduct = (handle) => {
    return fetch(`/products/${ handle }.js`)
    .then((response) => response.json())
    .then((product) => {
      this.product = product;
      this.selectedVariant = product.variants[0];

      return product;
    });
  }

  updateCostPerServe(group) {
    if (this.productCostPerServe) {
      let costPerServe;
      if (group) {
        costPerServe = parseFloat(this.price(group.selected_selling_plan).replace('A$', '').replace('$', '')) / Number(this.productCostPerServe.getAttribute('data-serving-size'));
        this.productCostPerServe.innerHTML = '$' + Math.floor(costPerServe * 100) / 100 + ' per serve';
      } else {
        if (this.productOneTimeSaleDiscount) {
          this.productCostPerServe.innerHTML = this.productOneTimeSaleDiscount.productCostPerServe + ' per serve';
        } else {
          costPerServe = this.selectedVariant.price / 100 / Number(this.productCostPerServe.getAttribute('data-serving-size'));
          this.productCostPerServe.innerHTML = '$' + Math.floor(costPerServe * 100) / 100 + ' per serve';
        }
      }
    }
  }
}

customElements.define('skio-plan-picker', SkioPlanPickerComponent);